import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plug, Copy, Check, Trash2 } from 'lucide-react';
import { mcpApi, mcpEndpoint, McpToken } from '@/lib/mcp';
import { isPlanGateError } from '@/lib/planGate';
import { ConfirmModal } from '@/components/ConfirmModal';
import { useToast, errMessage } from '@/components/Toast';

/**
 * MCP connector settings (W2-4). Generate/revoke per-workspace tokens that let
 * Claude / ChatGPT / Copilot query this workspace's finance data over the Model
 * Context Protocol. The token is shown once on creation. Growth+.
 */
export function McpSection({ wsId }: { wsId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [created, setCreated] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [revokeId, setRevokeId] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const { data: tokens = [] } = useQuery({
    queryKey: ['mcp-tokens', wsId],
    queryFn: () => mcpApi.list(wsId),
    enabled: !!wsId,
    retry: false,
  });

  const create = useMutation({
    mutationFn: () => mcpApi.create(wsId, name.trim() || 'MCP token'),
    onSuccess: (r) => {
      setCreated(r.token);
      setName('');
      qc.invalidateQueries({ queryKey: ['mcp-tokens', wsId] });
    },
    onError: (e) => {
      if (isPlanGateError(e)) toast.error(t('mcp.gate', { defaultValue: 'MCP access is a Growth feature — upgrade to generate a token.' }));
      else toast.error(errMessage(e, t('mcp.error', { defaultValue: 'Could not create the token.' })));
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => mcpApi.revoke(wsId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mcp-tokens', wsId] }),
  });

  const endpoint = mcpEndpoint();
  const copy = (text: string, key: string) => {
    navigator.clipboard?.writeText(text).then(() => { setCopied(key); setTimeout(() => setCopied(null), 1500); }).catch(() => undefined);
  };

  return (
    <section className="rounded-2xl border border-ink-200 bg-white p-6">
      <div className="flex items-center gap-2 mb-1">
        <Plug className="h-4 w-4 text-emerald-700" />
        <h2 className="text-sm font-semibold text-ink-900">{t('mcp.title', { defaultValue: 'AI connector (MCP)' })}</h2>
      </div>
      <p className="text-[12.5px] text-ink-600 mb-4">
        {t('mcp.subtitle', { defaultValue: 'Let Claude, ChatGPT or Copilot read this workspace’s finance data over the Model Context Protocol. Generate a token, then add the endpoint to your AI client.' })}
      </p>

      {/* Endpoint */}
      <div className="mb-4">
        <span className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-1">{t('mcp.endpoint', { defaultValue: 'MCP endpoint' })}</span>
        <div className="flex items-center gap-2">
          <code className="flex-1 num text-[12px] rounded border border-border bg-ink-50 px-2 py-1.5 text-ink-800 truncate">{endpoint}</code>
          <button onClick={() => copy(endpoint, 'ep')} className="inline-flex items-center gap-1 rounded border border-border px-2 py-1.5 text-[11px] text-ink-700 hover:bg-ink-50">
            {copied === 'ep' ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>

      {/* Just-created token (shown once) */}
      {created && (
        <div className="mb-4 rounded-lg border border-emerald-300 bg-emerald-50/60 p-3">
          <p className="text-[12px] font-semibold text-emerald-800 mb-1">{t('mcp.created_once', { defaultValue: 'Copy this token now — it won’t be shown again.' })}</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 num text-[12px] rounded border border-emerald-300 bg-white px-2 py-1.5 text-ink-900 truncate">{created}</code>
            <button onClick={() => copy(created, 'tok')} className="inline-flex items-center gap-1 rounded border border-emerald-300 bg-white px-2 py-1.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50">
              {copied === 'tok' ? <><Check className="h-3.5 w-3.5" /> {t('mcp.copied', { defaultValue: 'Copied' })}</> : <><Copy className="h-3.5 w-3.5" /> {t('common.copy', { defaultValue: 'Copy' })}</>}
            </button>
          </div>
          <button onClick={() => setCreated(null)} className="mt-2 text-[11px] text-ink-600 hover:text-ink-900">{t('mcp.dismiss', { defaultValue: 'I’ve saved it' })}</button>
        </div>
      )}

      {/* Create */}
      <div className="flex items-center gap-2 mb-4">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('mcp.name_placeholder', { defaultValue: 'Token name (e.g. Claude desktop)' })}
          className="flex-1 rounded-md border border-border px-3 py-1.5 text-sm"
        />
        <button
          onClick={() => create.mutate()}
          disabled={create.isPending}
          className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
        >
          <Plug className="h-3.5 w-3.5" /> {create.isPending ? t('mcp.generating', { defaultValue: 'Generating…' }) : t('mcp.generate', { defaultValue: 'Generate token' })}
        </button>
      </div>

      {/* Token list */}
      {tokens.length > 0 && (
        <ul className="divide-y divide-border rounded-md border border-border">
          {tokens.map((tok: McpToken) => (
            <li key={tok.id} className="flex items-center justify-between px-3 py-2">
              <div className="min-w-0">
                <span className="text-[13px] font-medium text-ink-800 truncate">{tok.name}</span>
                <span className="block text-[11px] text-muted-foreground">
                  {t('mcp.created_at', { defaultValue: 'created' })} {new Date(tok.created_at).toLocaleDateString()}
                  {tok.last_used_at ? ` · ${t('mcp.last_used', { defaultValue: 'last used' })} ${new Date(tok.last_used_at).toLocaleDateString()}` : ` · ${t('mcp.never_used', { defaultValue: 'never used' })}`}
                </span>
              </div>
              <button onClick={() => setRevokeId(tok.id)} className="inline-flex items-center gap-1 text-[11px] text-ink-500 hover:text-rose-600">
                <Trash2 className="h-3.5 w-3.5" /> {t('mcp.revoke', { defaultValue: 'Revoke' })}
              </button>
            </li>
          ))}
        </ul>
      )}

      <ConfirmModal
        open={revokeId !== null}
        title={t('mcp.revoke_confirm', { defaultValue: 'Revoke this MCP token? Any client using it will lose access immediately.' })}
        confirmLabel={t('mcp.revoke', { defaultValue: 'Revoke' })}
        destructive
        onConfirm={() => { if (revokeId) revoke.mutate(revokeId); setRevokeId(null); }}
        onCancel={() => setRevokeId(null)}
      />
    </section>
  );
}
